import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { withTimeout } from '@/lib/withTimeout';

// Widened from a fixed union to a plain string: roles are no longer capped
// at 4 hardcoded values — public.custom_roles is the real registry now, and
// the founder can add new ones from the Team page at any time.
export type UserRole = string | null;

export interface UserRoleContextValue {
  role: UserRole;
  loading: boolean;
  user: User | null;
}

const UserRoleContext = createContext<UserRoleContextValue | undefined>(undefined);

// Only fires for staff (a resolved user_roles row) — this provider also
// mounts for every signed-in retail customer (it shares the same auth
// session as AuthContext), and logging every storefront login here would
// flood the admin hub's Login Log with retail traffic instead of the staff
// sign-ins it's actually meant to show.
async function trackLogin(action: 'login' | 'logout', accessToken: string) {
  try {
    await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/track-login`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, portal: 'rxk' }),
    });
  } catch (err) {
    console.warn('track-login failed', err);
  }
}

function todayLoginKey(userId: string): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `login_tracked_${userId}_${stamp}`;
}

// Mounted once at the app root (see App.tsx) instead of being fetched by
// every consumer (RequireRole, AppShell, and several admin pages all used
// to call this independently). Every admin route is a separate top-level
// React Router route, so navigating admin -> admin unmounts/remounts the
// whole page tree; with a per-component hook, that meant 2-3 concurrent
// getSession()+user_roles fetches racing on every click, and RequireRole
// bouncing to "/" (looking exactly like being logged out) whenever its own
// copy of that race lost. One shared fetch, kept alive across navigation,
// removes the race entirely.
export function UserRoleProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchRole = async (sessionUser: User | null): Promise<UserRole> => {
      if (!sessionUser) {
        if (mounted) {
          setUser(null);
          setRole(null);
          setLoading(false);
        }
        return null;
      }
      if (mounted) setUser(sessionUser);

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', sessionUser.id)
        .maybeSingle();

      const resolvedRole = error ? null : ((data?.role as UserRole) ?? null);
      if (mounted) {
        setRole(resolvedRole);
        setLoading(false);
      }
      return resolvedRole;
    };

    withTimeout(supabase.auth.getSession(), 6000, { data: { session: null }, error: null }).then(({ data: { session } }) =>
      fetchRole(session?.user ?? null),
    );

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Do NOT await/return this from the callback — supabase-js awaits every
      // onAuthStateChange subscriber before signInWithPassword() (and other
      // auth calls) resolve. fetchRole runs a DB query, so without deferring
      // it here, sign-in itself would hang until that query finishes.
      setTimeout(async () => {
        const resolvedRole = await fetchRole(session?.user ?? null);
        if (resolvedRole && session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
          const key = todayLoginKey(session.user.id);
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, '1');
            trackLogin('login', session.access_token);
          }
        }
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return <UserRoleContext.Provider value={{ role, loading, user }}>{children}</UserRoleContext.Provider>;
}

export function useUserRoleContext(): UserRoleContextValue {
  const ctx = useContext(UserRoleContext);
  if (!ctx) throw new Error('useUserRoleContext must be used within <UserRoleProvider>');
  return ctx;
}
